// src/pages/Take5JHA.tsx — G-12: Mobile Take 5 / JHA Field Submission
// Field-optimised pre-task hazard identification. GPS-stamped, links to SWMS.
// Submitted records visible to WHSO in WHSInspections.
// CoP: How to Manage WHS Risks (Safe Work Australia) · TMR Site Safety Plan

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_TAKE5, Take5Record } from '../utils/mockDataWHSMgmt'
import { MOCK_JOBS } from '../utils/mockData'
import { Shield, Plus, CheckCircle, Clock, MapPin, ChevronRight, X, AlertTriangle, Search, Camera, Download } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

const fmtDateTime = (s: string) => new Date(s).toLocaleString('en-AU', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })

// Pre-populated hazard/control pairs from site hazard register
const QUICK_HAZARDS = [
  { hazard: 'Energised conductors', control: 'LOTO applied and verified by authorised person before work commences' },
  { hazard: 'Confined space entry', control: 'CS entry permit issued. Atmospheric test completed. Standby person assigned.' },
  { hazard: 'Traffic exposure', control: 'Approved TMP in place. Licensed TC officers on all approaches. High-vis PPE worn.' },
  { hazard: 'Excavation depth >1.5m', control: 'DBYD plans reviewed and marked. Benching/shoring in place. SWMS followed.' },
  { hazard: 'Underground services', control: 'Vacuum excavation for first 500mm around any service marks. 500mm hand-dig zone.' },
  { hazard: 'Working at height >2m', control: 'EWP pre-start inspection completed. Fall arrest inspected. No-go zone below work area.' },
  { hazard: 'Manual handling', control: 'Team lift for loads >16kg. Mechanical assist (trolley/crane) where available.' },
  { hazard: 'Night works / reduced visibility', control: 'Adequate lighting provided. High-vis Class D. Spotter assigned for plant movement.' },
  { hazard: 'Overhead power lines', control: 'Safe approach distance maintained (3m+ for HV). No craning near powerlines without ESO clearance.' },
  { hazard: 'Chemical / hazardous substance', control: 'SDS reviewed. Required PPE worn. Spill kit on site. Ventilation confirmed.' },
]

// ── New Take 5 form ───────────────────────────────────────────────
function NewTake5Modal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (r: Take5Record) => void }) {
  const { user } = useAuthStore()
  const [jobId, setJobId]   = useState(MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))[0]?.id ?? '')
  const [task, setTask]     = useState('')
  const [location, setLocation] = useState('')
  const [selectedHazards, setSelectedHazards] = useState<string[]>([])
  const [selectedControls, setSelectedControls] = useState<string[]>([])
  const [customHazard, setCustomHazard] = useState('')
  const [customControl, setCustomControl] = useState('')
  const [swmsRef, setSwmsRef] = useState('')
  const [signed, setSigned]   = useState(false)
  const activeJobs = MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))

  const toggleHazard = (h: string, c: string) => {
    if (selectedHazards.includes(h)) {
      setSelectedHazards(p => p.filter(x => x !== h))
      setSelectedControls(p => p.filter(x => x !== c))
    } else {
      setSelectedHazards(p => [...p, h])
      setSelectedControls(p => [...p, c])
    }
  }

  const addCustom = () => {
    if (!customHazard.trim() || !customControl.trim()) return
    setSelectedHazards(p => [...p, customHazard.trim()])
    setSelectedControls(p => [...p, customControl.trim()])
    setCustomHazard(''); setCustomControl('')
  }

  const canSubmit = task.trim() && location.trim() && selectedHazards.length > 0 && signed

  const handleSubmit = () => {
    const job = activeJobs.find(j => j.id === jobId)
    onSubmit({
      id: `t5-${Date.now()}`, jobId, jobNumber: job?.jobNumber ?? '',
      workerName: user?.fullName ?? 'Field Worker', workerId: user?.id ?? 'u0',
      submittedAt: new Date().toISOString(),
      taskDescription: task, location,
      gpsCoords: '-27.3827, 153.0680', // simulated GPS
      hazardsIdentified: selectedHazards, controlsSelected: selectedControls,
      linkedSwmsRef: swmsRef || null, workerSignature: user?.fullName ?? 'Field Worker',
      status: 'SUBMITTED', reviewedByName: null, reviewedAt: null,
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-6 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="bg-[#1F3864] px-6 py-4 flex items-center justify-between">
          <div><h3 className="font-bold text-white">Take 5 / JHA — Pre-task Check</h3><p className="text-blue-200 text-xs mt-0.5">Complete before commencing any high-risk task</p></div>
          <button onClick={onClose}><X className="w-4 h-4 text-white/70"/></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Job + task */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Job site <span className="text-red-500">*</span></label>
              <select className="input text-sm w-full" value={jobId} onChange={e => setJobId(e.target.value)}>
                {activeJobs.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Linked SWMS ref</label>
              <input className="input text-sm w-full" value={swmsRef} onChange={e => setSwmsRef(e.target.value)} placeholder="e.g. SWMS-TP001-001"/>
            </div>
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Task / activity <span className="text-red-500">*</span></label>
              <input className="input text-sm w-full" value={task} onChange={e => setTask(e.target.value)} placeholder="Describe the specific task you are about to perform"/>
            </div>
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Exact location on site <span className="text-red-500">*</span></label>
              <div className="relative"><MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"/>
                <input className="input text-sm w-full pl-9" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Pit 4B, northbound lane, Sandgate Rd"/>
              </div>
              <div className="text-xs text-green-600 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3"/>GPS coordinates captured: -27.3827, 153.0680 (simulated)</div>
            </div>
          </div>

          {/* Hazard identification */}
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-2">Identify hazards and select controls <span className="text-red-500">*</span></div>
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {QUICK_HAZARDS.map(({ hazard, control }) => {
                const checked = selectedHazards.includes(hazard)
                return (
                  <label key={hazard} className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer border transition-colors ${checked ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleHazard(hazard, control)} className="mt-0.5 accent-[#2E75B6] flex-shrink-0"/>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{hazard}</div>
                      {checked && <div className="text-xs text-blue-700 mt-0.5">Control: {control}</div>}
                    </div>
                  </label>
                )
              })}
            </div>
            {/* Custom hazard */}
            <div className="mt-2 border border-dashed border-gray-300 rounded-lg p-3 space-y-2">
              <div className="text-xs font-medium text-gray-600">Add custom hazard</div>
              <input className="input text-xs w-full" value={customHazard} onChange={e => setCustomHazard(e.target.value)} placeholder="Hazard description"/>
              <input className="input text-xs w-full" value={customControl} onChange={e => setCustomControl(e.target.value)} placeholder="Control measure to be applied"/>
              <button onClick={addCustom} disabled={!customHazard.trim() || !customControl.trim()} className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-50">+ Add</button>
            </div>
          </div>

          {selectedHazards.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-xs text-green-800">
              <div className="font-semibold mb-1">{selectedHazards.length} hazard{selectedHazards.length !== 1 ? 's' : ''} identified — controls confirmed</div>
              {selectedHazards.map((h, i) => <div key={h} className="flex items-start gap-1.5"><CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5"/><span>{h}</span></div>)}
            </div>
          )}

          {/* Worker declaration */}
          <div className={`border rounded-xl p-4 ${signed ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-200'}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={signed} onChange={e => setSigned(e.target.checked)} className="mt-0.5 accent-green-600 w-4 h-4 flex-shrink-0"/>
              <div className="text-xs text-gray-700">
                <strong>Worker declaration:</strong> I have read and understood the relevant SWMS. I have identified the hazards listed above and the controls are in place before I commence work. I am fit for duty and will stop work if conditions change.
                {signed && <div className="text-green-700 font-semibold mt-1">✓ Signed: {user?.fullName} — {new Date().toLocaleString('en-AU', {day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</div>}
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
              <CheckCircle className="w-4 h-4"/>Submit Take 5
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function Take5JHA() {
  const { isTier } = useAuthStore()
  // C_WHSO is the primary reviewer of Take 5 submissions; PM/Admin can also review
  const canReview = isTier('B_PC') || isTier('B_PM') || isTier('A_DEVELOPER') || isTier('C_WHSO')
  const [records, setRecords] = useState<Take5Record[]>(MOCK_TAKE5)
  const [selected, setSelected] = useState<Take5Record | null>(null)
  const [showNew, setShowNew] = useState(false)
  const [jobFilter, setJobFilter] = useState('ALL')
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'SUBMITTED' | 'REVIEWED'>('ALL')

  const queryClient = useQueryClient()

  // Persist Take 5 records to Neon via the existing WHS inspections endpoint.
  // Maps title = "Take 5 — <task>", checklist = hazard/control pairs.
  // A dedicated take5_records table can be added in a later migration if needed.
  const saveMutation = useMutation({
    mutationFn: async (record: Take5Record) => {
      const res = await fetch(
        (import.meta.env.VITE_API_URL ?? 'http://localhost:8787') + '/api/whs/inspections',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jobId:        record.jobId,
            title:        `Take 5 — ${record.taskDescription}`,
            inspectedAt:  record.submittedAt,
            overallPass:  true,
            checklist:    record.hazardsIdentified.map((h, i) => ({
              item:   h,
              status: 'pass',
              notes:  record.controlsSelected[i] ?? '',
            })),
            notes: [
              `GPS: ${record.gpsCoords ?? 'N/A'}`,
              `Signed by: ${record.workerSignature}`,
              `SWMS ref: ${record.linkedSwmsRef ?? 'None'}`,
              `Location: ${record.location}`,
            ].join(' | '),
          }),
        }
      )
      if (!res.ok) console.warn('[Take5] API save failed — record retained locally')
      return res.ok ? res.json() : null
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['whs-inspections'] }),
  })

  const activeJobs = MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))
  const filtered = records.filter(r =>
    (jobFilter === 'ALL' || r.jobId === jobFilter) &&
    (statusFilter === 'ALL' || r.status === statusFilter)
  )
  const unreviewed = records.filter(r => r.status === 'SUBMITTED').length

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Shield className="w-6 h-6 text-amber-500"/>Take 5 / JHA Register</h1>
          <p className="text-sm text-gray-500 mt-0.5">Pre-task hazard identification · GPS-stamped · linked to SWMS · field mobile form</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowNew(true)} className="btn-primary text-sm flex items-center gap-2"><Plus className="w-4 h-4"/>New Take 5</button>
          <button className="btn-secondary text-sm flex items-center gap-2"><Download className="w-4 h-4"/>Export</button>
        </div>
      </div>

      {unreviewed > 0 && canReview && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
          <Clock className="w-4 h-4 text-amber-600 flex-shrink-0"/>
          <span><strong>{unreviewed} Take 5 submission{unreviewed !== 1 ? 's' : ''}</strong> awaiting WHSO review.</span>
          <button onClick={() => setStatusFilter('SUBMITTED')} className="ml-auto text-xs underline">View</button>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800">
        <div className="font-semibold mb-1">How Take 5 works on this platform</div>
        Workers complete a Take 5 on their mobile device before starting any high-risk task. Records are GPS-stamped and linked to the relevant SWMS. WHSO receives a notification for review. All submitted Take 5s are visible here and in the WHSInspections module as 'Take 5' inspection type.
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-3">
        {[{ label:'Submitted today', value:records.filter(r=>r.submittedAt.startsWith('2026-07-18')).length, color:'text-blue-700', bg:'bg-blue-50 border-blue-200' },
          { label:'Pending review', value:unreviewed, color:unreviewed>0?'text-amber-700':'text-gray-400', bg:unreviewed>0?'bg-amber-50 border-amber-200':'bg-white border-gray-200' },
          { label:'Reviewed (all time)', value:records.filter(r=>r.status==='REVIEWED').length, color:'text-green-700', bg:'bg-green-50 border-green-200' }].map(m => (
          <div key={m.label} className={`border rounded-xl px-4 py-3 ${m.bg}`}><div className={`text-2xl font-bold ${m.color}`}>{m.value}</div><div className="text-xs font-medium text-gray-700 mt-0.5">{m.label}</div></div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <select className="input text-sm w-auto" value={jobFilter} onChange={e => setJobFilter(e.target.value)}>
          <option value="ALL">All job sites</option>
          {activeJobs.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
        </select>
        {(['ALL','SUBMITTED','REVIEWED'] as const).map(f => (
          <button key={f} onClick={() => setStatusFilter(f)} className={`text-xs px-3 py-1.5 rounded-full font-medium ${statusFilter===f?'bg-[#1F3864] text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {f==='ALL'?`All (${records.length})`:f==='SUBMITTED'?`Pending review (${unreviewed})`:`Reviewed (${records.filter(r=>r.status==='REVIEWED').length})`}
          </button>
        ))}
      </div>

      {/* Record list */}
      <div className="flex gap-5 items-start">
        <div className={`${selected ? 'w-1/2' : 'w-full'} space-y-2 transition-all`}>
          {filtered.map(r => {
            const isSel = selected?.id === r.id
            return (
              <div key={r.id} onClick={() => setSelected(isSel ? null : r)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSel ? 'border-blue-400 ring-2 ring-blue-100' : r.status==='SUBMITTED'?'border-amber-300':'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.status==='REVIEWED'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>{r.status==='REVIEWED'?'Reviewed ✓':'Pending review'}</span>
                      <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-mono">{r.jobNumber}</span>
                      {r.linkedSwmsRef && <span className="text-[10px] text-gray-400">{r.linkedSwmsRef}</span>}
                    </div>
                    <div className="font-semibold text-gray-900 text-sm">{r.taskDescription}</div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/>{r.location}</span>
                      <span>{r.workerName}</span>
                      <span>{fmtDateTime(r.submittedAt)}</span>
                    </div>
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      {r.hazardsIdentified.map(h => <span key={h} className="text-[10px] bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded">{h}</span>)}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSel?'text-blue-500':'text-gray-400'}`}/>
                </div>
              </div>
            )
          })}
          {filtered.length === 0 && <div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">No Take 5 records match filters</div>}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="w-1/2 sticky top-6 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <div className="px-5 py-4 border-b bg-gray-50 flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${selected.status==='REVIEWED'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}`}>{selected.status==='REVIEWED'?'Reviewed ✓':'Pending review'}</span>
                  <span className="text-xs font-mono text-gray-400">{selected.jobNumber}</span>
                </div>
                <div className="font-semibold text-gray-900 text-sm">{selected.taskDescription}</div>
              </div>
              <button onClick={() => setSelected(null)}><X className="w-4 h-4 text-gray-400"/></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-gray-500 mb-0.5">Worker</div><div className="font-medium">{selected.workerName}</div></div>
                <div><div className="text-xs text-gray-500 mb-0.5">Submitted</div><div className="font-medium">{fmtDateTime(selected.submittedAt)}</div></div>
                <div className="col-span-2"><div className="text-xs text-gray-500 mb-0.5">Location</div><div className="font-medium flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400"/>{selected.location}</div></div>
                {selected.gpsCoords && <div className="col-span-2"><div className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3"/>GPS: {selected.gpsCoords}</div></div>}
                {selected.linkedSwmsRef && <div className="col-span-2"><div className="text-xs text-gray-500 mb-0.5">Linked SWMS</div><div className="text-xs font-mono text-blue-600">{selected.linkedSwmsRef}</div></div>}
              </div>

              <div>
                <div className="text-xs font-semibold text-gray-700 mb-2">Hazards identified & controls</div>
                {selected.hazardsIdentified.map((h, i) => (
                  <div key={h} className="mb-2 bg-gray-50 rounded-lg p-3">
                    <div className="text-xs font-medium text-orange-800 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-orange-500"/>{h}</div>
                    <div className="text-xs text-green-700 mt-1 flex items-start gap-1"><CheckCircle className="w-3 h-3 flex-shrink-0 mt-0.5"/>Control: {selected.controlsSelected[i] ?? '—'}</div>
                  </div>
                ))}
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-800">
                <CheckCircle className="w-3.5 h-3.5 inline mr-1"/>Worker declaration signed: <strong>{selected.workerSignature}</strong>
              </div>

              {selected.status === 'SUBMITTED' && canReview && (
                <button onClick={() => {
                  setRecords(p => p.map(r => r.id === selected.id ? { ...r, status:'REVIEWED', reviewedByName:'Jane Smith', reviewedAt:new Date().toISOString() } : r))
                  setSelected(s => s ? { ...s, status:'REVIEWED', reviewedByName:'Jane Smith', reviewedAt:new Date().toISOString() } : null)
                }} className="w-full btn-primary text-sm flex items-center justify-center gap-2">
                  <CheckCircle className="w-4 h-4"/>Mark as Reviewed (WHSO)
                </button>
              )}
              {selected.status === 'REVIEWED' && (
                <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  Reviewed by {selected.reviewedByName} — {selected.reviewedAt ? fmtDateTime(selected.reviewedAt) : ''}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showNew && <NewTake5Modal onClose={() => setShowNew(false)} onSubmit={r => {
        setRecords(p => [r, ...p])
        saveMutation.mutate(r)   // persist to Neon via inspections endpoint; graceful fallback to local
        setShowNew(false)
      }}/>}
    </div>
  )
}
